import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CreateInvoiceRequest,
  HeldBillResponse,
  HoldBillRequest,
  InvoiceResponse,
  PaymentRequest,
  RestoreHeldBillResponse,
  SalesReturnRequest,
  SalesReturnResponse,
} from '@/types/billing';
import type { PosProductScanResponse } from '@/types/storeProduct';

export const billingApi = {
  listInvoices: (
    params?: SearchParams & { storeId?: number; from?: string; to?: string },
  ) =>
    unwrap(
      api.get<ApiResponse<PageResponse<InvoiceResponse>>>('/v1/billing/invoices', { params }),
    ),

  getInvoice: (id: number) =>
    unwrap(api.get<ApiResponse<InvoiceResponse>>(`/v1/billing/invoices/${id}`)),

  lookupInvoiceForReturn: (storeId: number, q: string) =>
    unwrap(
      api.get<ApiResponse<InvoiceResponse>>('/v1/billing/invoices/lookup', {
        params: { storeId, q },
      }),
    ),

  cancelHeldBill: (id: number) =>
    unwrap(api.post<ApiResponse<void>>(`/v1/billing/hold/${id}/cancel`)),

  scanProduct: (storeId: number, barcode: string) =>
    unwrap(
      api.get<ApiResponse<PosProductScanResponse>>('/v1/billing/scan', {
        params: { storeId, barcode },
      }),
    ),

  createInvoice: (body: CreateInvoiceRequest) =>
    unwrap(api.post<ApiResponse<InvoiceResponse>>('/v1/billing/invoices', body)),

  addPayment: (id: number, body: PaymentRequest) =>
    unwrap(api.post<ApiResponse<InvoiceResponse>>(`/v1/billing/invoices/${id}/payments`, body)),

  holdBill: (body: HoldBillRequest) =>
    unwrap(api.post<ApiResponse<HeldBillResponse>>('/v1/billing/hold', body)),

  listHeldBills: (storeId: number) =>
    unwrap(api.get<ApiResponse<HeldBillResponse[]>>('/v1/billing/hold', { params: { storeId } })),

  restoreHeldBill: (id: number) =>
    unwrap(api.post<ApiResponse<RestoreHeldBillResponse>>(`/v1/billing/hold/${id}/resume`)),

  printInvoice: (id: number) =>
    api.get(`/v1/billing/invoices/${id}/print`, { responseType: 'text' }).then((r) => r.data as string),

  openPrintWindow: async (id: number) => {
    const html = await billingApi.printInvoice(id);
    const w = window.open('', '_blank', 'width=420,height=720');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  },

  downloadInvoicePdf: async (id: number) => {
    const response = await api.get(`/v1/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${id}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  },

  processSalesReturn: (body: SalesReturnRequest) =>
    unwrap(api.post<ApiResponse<SalesReturnResponse>>('/v1/billing/returns', body)),

  /** Evaluate auto-apply promotions for current cart (live preview). */
  evaluatePromotions: (storeId: number, cartTotal: number, items: unknown[]) =>
    unwrap(api.post<ApiResponse<{ discountAmount: number }>>('/v1/billing/promotions/evaluate', {
      storeId,
      cartTotal,
      items,
    })),

  /** List all currently active promotions for a store (for cashier panel display). */
  listActivePromotions: (storeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>('/v1/pos/promotions', { params: { storeId } })),

  voidInvoice: (id: number, reason: string, approvalInstanceId?: number) =>
    unwrap(
      api.post<ApiResponse<InvoiceResponse>>(`/v1/billing/invoices/${id}/void`, {
        reason,
        approvalInstanceId,
      }),
    ),

  cancelVoid: (id: number, reason?: string) =>
    unwrap(
      api.post<ApiResponse<InvoiceResponse>>(`/v1/billing/invoices/${id}/cancel-void`, null, {
        params: { reason },
      }),
    ),
};
